import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload } from "lucide-react";
import { GradePill, HW_STATUS_STYLES, RATING_STYLES } from "../teacher/progress";

/* ------------------ Homework (student: view + submit + feedback) ------------------ */
export function StudentHomeworkTab() {
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [text, setText] = useState("");

  const load = () => api.get("/my-homework").then((r) => setList(r.data)).catch(() => setList([]));
  useEffect(() => { load(); }, []);

  async function submit() {
    if (!text.trim()) { toast.error("Write your answer before submitting"); return; }
    try {
      await api.post(`/homework/${active.id}/submit`, { text });
      toast.success("Homework submitted");
      setActive(null); setText("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2">
        {list.length === 0 && <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center col-span-full">No homework assigned yet.</Card>}
        {list.map((h) => (
          <Card key={h.id} data-testid={`student-hw-${h.id}`} className="p-6 rounded-2xl border-slate-200">
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs uppercase tracking-widest text-[#B8860B]">Assigned {(h.assigned_date || "").slice(0, 10)}</div>
              <Badge className={`rounded-full border-0 capitalize ${HW_STATUS_STYLES[h.submission_status]}`}>{h.submission_status}</Badge>
            </div>
            <div className="mt-1 font-serif font-black text-lg text-[#0F1E4F]">{h.title}</div>
            <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{h.description}</p>
            {h.notes && <p className="mt-2 text-sm text-slate-500 whitespace-pre-line">Teacher note: {h.notes}</p>}
            <div className="mt-3 text-xs text-slate-500">Due: {h.due_date || "No due date"} · Total marks: {h.total_marks ?? 100}</div>

            {h.submission?.graded && (
              <div className="mt-4 rounded-xl bg-emerald-50 p-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
                  Score {h.submission.marks_obtained}/{h.submission.total_marks} · {h.submission.percentage}%
                  <GradePill letter={h.submission.letter_grade} />
                </div>
                {h.submission.feedback && <p className="mt-1.5 text-sm text-emerald-900/80 whitespace-pre-line">Feedback: {h.submission.feedback}</p>}
              </div>
            )}

            {!h.submission?.graded && (
              <Button data-testid={`student-hw-submit-${h.id}`} className="mt-4 rounded-full bg-[#0F1E4F] text-white"
                onClick={() => { setActive(h); setText(h.submission?.text || ""); }}>
                <Upload className="h-4 w-4 mr-1.5" /> {h.submission ? "Update submission" : "Submit homework"}
              </Button>
            )}
            {h.submission && !h.submission.graded && (
              <div className="mt-2 text-xs text-slate-500">Submitted {(h.submission.submitted_at || "").slice(0, 10)} — awaiting grading</div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle>Submit — {active?.title}</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <Textarea data-testid="student-hw-text" rows={7} placeholder="Type your answer / notes" value={text} onChange={(e) => setText(e.target.value)} className="rounded-xl" />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setActive(null)}>Cancel</Button>
            <Button data-testid="student-hw-send" onClick={submit} className="rounded-full bg-[#0F1E4F] text-white">Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ------------------ Exam results (student) ------------------ */
export function StudentExamResultsTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/exam-results").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const avg = list.length ? Math.round(list.reduce((a, x) => a + (x.percentage || 0), 0) / list.length * 10) / 10 : null;

  return (
    <div className="space-y-4">
      <Card className="p-6 rounded-2xl border-slate-200">
        <div className="text-xs uppercase tracking-widest text-[#B8860B]">Average exam score</div>
        <div data-testid="student-exam-average" className="mt-2 font-serif text-3xl font-black text-[#0F1E4F]">{avg == null ? "—" : `${avg}%`}</div>
      </Card>
      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        {list.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No exam results published yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Exam</TableHead><TableHead>Date</TableHead><TableHead>Score</TableHead><TableHead>%</TableHead><TableHead>Grade</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((x) => (
                <TableRow key={x.id} data-testid={`student-result-${x.id}`}>
                  <TableCell className="font-semibold text-[#0F1E4F]">{x.exam_title}</TableCell>
                  <TableCell>{x.exam_date}</TableCell>
                  <TableCell>{x.marks_obtained}/{x.total_marks}</TableCell>
                  <TableCell>{x.percentage}%</TableCell>
                  <TableCell><GradePill letter={x.letter_grade} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/* ------------------ Aggregated progress (student) ------------------ */
export function StudentProgressSummaryTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/progress/summary/me").then((r) => setS(r.data)).catch(() => setS(null)); }, []);
  if (!s) return <Card className="p-10 rounded-2xl border-dashed border-slate-300 text-center text-slate-500 text-sm">Progress data is not available yet.</Card>;

  const metrics = [
    ["Attendance", s.attendance_percentage],
    ["Exam average", s.exam_average_percentage],
    ["Homework completion", s.homework_completion_percentage],
    ["Homework average", s.homework_average_grade],
    ["Overall progress", s.overall_progress],
    ["Book progress", s.current_book_progress],
  ];

  return (
    <div className="space-y-5" data-testid="student-progress-summary">
      <Card className="p-6 rounded-2xl border-0 text-white bg-gradient-to-br from-[#0F1E4F] to-[#1E3A8A]">
        <div className="text-xs uppercase tracking-widest text-[#F5D06B]">Overall academic progress</div>
        <div className="mt-1 flex items-end gap-3">
          <div className="font-serif text-4xl font-black">{s.overall_progress == null ? "—" : `${s.overall_progress}%`}</div>
          {s.overall_letter_grade && <GradePill letter={s.overall_letter_grade} />}
        </div>
        <div className="mt-2 text-white/70 text-sm">Current book: {s.current_course || "Not assigned"} · {s.completed_books_count} book(s) completed</div>
      </Card>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {metrics.map(([k, v]) => (
          <Card key={k} className="p-5 rounded-2xl border-slate-200">
            <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-2 font-serif text-2xl font-black text-[#0F1E4F]">{v == null ? "—" : `${v}%`}</div>
            <Progress value={v || 0} className="h-1.5 mt-2" />
          </Card>
        ))}
      </div>

      <Card className="p-6 rounded-2xl border-slate-200">
        <div className="text-xs uppercase tracking-widest text-[#B8860B]">Details</div>
        <div className="mt-3 grid gap-2 text-sm">
          {[
            ["Latest progress rating", s.progress_report_rating || "—"],
            ["Exams taken", s.exams_count],
            ["Homework submitted", `${s.homework_submitted}/${s.homework_assigned}`],
            ["Attendance records", s.attendance_records],
            ["Completed books", s.completed_books.length ? s.completed_books.join(", ") : "—"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between border-b border-slate-100 py-1.5">
              <span className="text-xs uppercase tracking-wider text-slate-500">{k}</span>
              <span className="font-semibold text-[#0F1E4F]">{v}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

/* ------------------ Progress reports (student, read-only) ------------------ */
export function StudentProgressReportsTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/progress-reports").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  return (
    <div className="grid gap-4 md:grid-cols-2">
      {list.length === 0 && <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center col-span-full">No progress reports yet.</Card>}
      {list.map((p) => (
        <Card key={p.id} data-testid={`student-report-${p.id}`} className="p-6 rounded-2xl border-slate-200">
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs uppercase tracking-widest text-[#B8860B]">{p.report_date} {p.period && `· ${p.period}`}</div>
            <Badge className={`rounded-full border-0 ${RATING_STYLES[p.overall_rating]}`}>{p.overall_rating}</Badge>
          </div>
          <div className="mt-1 font-serif font-black text-[#0F1E4F]">Report by {p.author_name || "Teacher"}</div>
          <dl className="mt-3 space-y-2 text-sm">
            {[["Comments", p.comments], ["Strengths", p.strengths], ["Areas for improvement", p.areas_for_improvement], ["Recommendations", p.recommendations]].map(([k, v]) => v ? (
              <div key={k}><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="text-slate-700 whitespace-pre-line">{v}</dd></div>
            ) : null)}
          </dl>
        </Card>
      ))}
    </div>
  );
}

/* ------------------ Today's lesson (student) ------------------ */
export function StudentLessonTab() {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/lessons").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  const today = new Date().toISOString().slice(0, 10);
  const todays = list.filter((l) => (l.date || "").slice(0, 10) === today);
  const rest = list.filter((l) => (l.date || "").slice(0, 10) !== today);

  const Item = ({ l, highlight }) => (
    <Card data-testid={`student-lesson-${l.id}`} className={`p-6 rounded-2xl ${highlight ? "border-[#0F1E4F]" : "border-slate-200"}`}>
      <div className="text-xs uppercase tracking-widest text-[#B8860B]">{l.date}</div>
      <div className="mt-1 font-serif font-black text-lg text-[#0F1E4F]">{l.title}</div>
      {l.topics && <div className="mt-2 text-sm text-slate-700"><span className="text-xs uppercase tracking-wider text-slate-500">Topics: </span>{l.topics}</div>}
      {l.description && <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{l.description}</p>}
      {l.notes && <p className="mt-2 text-sm text-slate-500 whitespace-pre-line">Notes: {l.notes}</p>}
    </Card>
  );

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Today</div>
        {todays.length === 0
          ? <Card className="p-8 rounded-2xl border-dashed border-slate-300 text-slate-500 text-sm text-center">No lesson posted for today yet.</Card>
          : <div className="grid gap-4 md:grid-cols-2">{todays.map((l) => <Item key={l.id} l={l} highlight />)}</div>}
      </div>
      {rest.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Previous lessons</div>
          <div className="grid gap-4 md:grid-cols-2">{rest.slice(0, 10).map((l) => <Item key={l.id} l={l} />)}</div>
        </div>
      )}
    </div>
  );
}
